// https://kapravelos.com/publications/vv8-imc19.pdf
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DOCUMENT_ATTRIBUTE_CHECK,
  DOCUMENT_CHECK,
  HEADLESS_CHROME_CHECK,
  LANGUAGES_CHECK,
  WEBDRIVER_CHECK,
  WINDOW_CHECK,
} from 'Lib/constants/detection';
import {
  I13N_ACTION_VIEW,
  I13N_CATEGORY_APP,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';

import { setAppStartInfo } from 'Store/appSettings/actions';
import { getBrowserFamily, getOsFamily } from 'Store/context/selectors';

// Not 100% reliable, just collecting informations
const documentAttributes = ['selenium', 'webdriver', 'driver'];
const headlessChromeUA = /HeadlessChrome/;
const huaweiUA = /HuaweiBrowser/;

const documentDetectionKeys = [
  '__driver_evaluate',
  '__driver_unwrapped',
  '__fxdriver_evaluate',
  '__fxdriver_unwrapped',
  '__selenium_evaluate',
  '__selenium_unwrapped',
  '__webdriver_evaluate',
  '__webdriver_script_fn',
  '__webdriver_script_func',
  '__webdriver_script_function',
  '__webdriver_unwrapped',
  '$cdc_asdjflasutopfhvcZLmcf', // Selenium + ChromeDriver
  '$cdc_asdjflasutopfhvcZLmcfl_',
  '$wdc_', // Selenium + WebDriver,
  'webdriver-evaluate',
  'webdriver-evaluate-response',
];

const windowDetectionKeys = [
  '__nightmare',
  '_phantom',
  '__phantomas',
  '_Selenium_IDE_Recorder',
  '_selenium',
  '_WEBDRIVER_ELEM_CACHE',
  'awesomium',
  'Buffer',
  'callPhantom',
  'callSelenium',
  'domAutomation', // ChromeDriver
  'domAutomationController',
  'emit',
  'geb', // https://github.com/geb/geb
  'spawn',
];

const documentAttributesCheck = () =>
  documentAttributes.some((attribute) => window.document.documentElement.getAttribute(attribute));
const documentCheck = () => documentDetectionKeys.some((check) => document?.[check]);
const headlessChromeCheck = ({ userAgent }: { userAgent: string }, isChrome: boolean) =>
  // @ts-expect-error
  headlessChromeUA.test(userAgent) || (isChrome && !window.chrome && !huaweiUA.test(userAgent)); // Huawei Chrome doesnt have window.chrome (Chrome plugins)
const languagesCheck = ({ languages }: { languages: string }) => languages === '';
const webdriverCheck = ({ webdriver }: { webdriver?: boolean }) => webdriver;
const windowCheck = () => windowDetectionKeys.some((check) => window?.[check]);

// Backend needs to release first if we want to add new rule
const checksMapping = {
  [DOCUMENT_ATTRIBUTE_CHECK]: documentAttributesCheck,
  [DOCUMENT_CHECK]: documentCheck,
  [HEADLESS_CHROME_CHECK]: headlessChromeCheck,
  [LANGUAGES_CHECK]: languagesCheck,
  [WEBDRIVER_CHECK]: webdriverCheck,
  [WINDOW_CHECK]: windowCheck,
};

const botDetect = (
  browser: string,
  os?: string
): {
  check: boolean;
  matches: string[];
} => {
  if (__QA__ || !__CLIENT__ || __DEVELOPMENT__) {
    // Don't detect in house automation
    return {
      check: false,
      matches: [],
    };
  }
  const isChrome = browser === 'Chrome' && os !== 'iOS'; // exclude webkit based Chrome
  const matches = Object.entries(checksMapping)
    .map(([key, func]) => {
      // @ts-expect-error
      const isBot = func(navigator, isChrome);
      if (isBot) {
        return key;
      }
      return undefined;
    })
    .filter(Boolean)
    .sort();

  return {
    check: matches.length > 0,
    matches, // breaches
  };
};

const useBotDetect = ({
  executeEvent,
}: {
  executeEvent: (eventName: string, value: Record<string, any>) => void;
}) => {
  const dispatch = useDispatch();
  const browser = useSelector(getBrowserFamily);
  const os = useSelector(getOsFamily);
  const beaconRef = useRef<boolean>();
  const { check, matches } = useMemo(() => botDetect(browser, os), [browser, os]);

  const sendBeacon = useCallback(() => {
    beaconRef.current = true;
    const message = matches.join(',');

    dispatch(setAppStartInfo(message));
    executeEvent?.(I13N_TYPE_FEATURE_COUNT, {
      category: I13N_CATEGORY_APP,
      action: I13N_ACTION_VIEW,
      label: 'App Starts',
      message: matches.join(','),
    });
  }, [dispatch, matches, executeEvent]);

  useEffect(() => {
    if (!beaconRef.current) {
      if (check) {
        sendBeacon();
      }
    }
  }, [check, sendBeacon, matches]);
};

export default useBotDetect;
